import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';

import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import LoadingDashboardIllustration from '@/views/auth/onboarding/assets/loading-dashboard.svg';
import UploadPlaceholder from '@/assets/images/upload-placeholder.svg';
import { useRefreshSession } from '@/states/common';
import { trackEvent } from '../../helpers/external-tools-helper';
import { APP_ROUTE } from '../../constants';
import {
  ApplicationContainer,
  ClientContainer,
  UserContainer,
} from '../../providers';
import { useCreateClient } from '../../hooks/api/client';

interface Business {
  id: string;
  name: string;
  logo?: string;
  access: {
    isPrimary: boolean;
  };
  clients?: Array<{
    id: string;
  }>;
}

interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirect?: boolean;
  onComplete?: () => void;
}

export function CreateWorkspaceModal({
  open,
  onOpenChange,
  redirect = true,
  onComplete,
}: CreateWorkspaceModalProps) {
  const { t } = useTranslation('workspace');
  const navigate = useNavigate();
  const { updateNewSelectedClient } = ClientContainer.useContainer();
  const { state: userState, setBusinessOptions } = UserContainer.useContainer();
  const createClient = useCreateClient();
  const { toast } = useToast();
  const [workspaceName, setWorkspaceName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const refreshSession = useRefreshSession();

  useEffect(() => {
    setWorkspaceName('');
  }, [open]);

  const triggerSuccessConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
      colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
    };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval: NodeJS.Timeout = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      // Create confetti bursts from multiple points
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  };

  const handleCreateWorkspace = async () => {
    setIsLoading(true);
    try {
      const clientDetails = await createClient({ name: workspaceName.trim() });
      if (clientDetails?.id) {
        triggerSuccessConfetti();

        onOpenChange(false);
        toast({
          variant: 'success',
          title: t(
            'workspace-added-successfully',
            'Workspace added successfully!'
          ),
          description: t(
            'workspace-added-successfully-description',
            'You can now add data sources to your workspace.'
          ),
        });
        trackEvent('new workspace', {
          name: workspaceName,
          id: clientDetails?.id,
        });

        updateNewSelectedClient(clientDetails);

        // IMPORTANT: Update business options to include the new client
        // This fixes the hasValidClientInUrl validation in switch-business-modal
        // that checks if the URL client ID exists in userState.businessOptions
        if (userState?.businessOptions && userState?.business) {
          const updatedBusinessOptions = userState.businessOptions.map(
            (business: Business) => {
              if (business.id === userState.business.id) {
                return {
                  ...business,
                  clients: [
                    ...(business.clients || []),
                    { id: clientDetails.id },
                  ],
                };
              }
              return business;
            }
          );
          setBusinessOptions(updatedBusinessOptions);
        }

        // await refreshSession();
        setIsLoading(false);
        onComplete?.();

        if (redirect) {
          navigate(
            `/ws/${clientDetails.id}/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}/add`
          );
        }
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('error-creating-workspace', 'Error creating workspace'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && workspaceName.trim().length && !isLoading) {
      handleCreateWorkspace();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl p-0 overflow-hidden min-h-[480px]">
        <DialogHeader className="border-b border-gray-200">
          <DialogTitle className="text-xl font-semibold p-4">
            {t('add-workspace', 'Add Workspace')}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 h-full">
          {/* Left side - Form */}
          <div className="p-6 bg-white">
            <p className="text-sm text-gray-500 mt-1">
              Create a new workspace to better manage the different data sources
              you have in ViewMetrics.
            </p>
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label htmlFor="workspace-name" className="text-sm font-medium">
                  {t('workspace-name', 'Workspace Name')}
                </Label>
                <Input
                  id="workspace-name"
                  placeholder="New workspace name"
                  value={workspaceName}
                  onChange={e => setWorkspaceName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Right side - Preview */}
          <div className="bg-gray-50 hidden md:block relative overflow-hidden">
            <div className="h-[24rem] flex items-center justify-center">
              {/* Background illustration */}
              <img
                className="absolute top-[5rem] left-1/2 transform -translate-x-[52%] -translate-y-[15%] w-full max-w-md rounded-xl p-[1.5rem] pr-0"
                src={LoadingDashboardIllustration}
                alt="Loading dashboard illustration"
              />

              {/* Floating workspace preview */}
              <div
                className={cn(
                  'relative z-10 p-5 bg-white rounded-xl border border-gray-200',
                  'w-full max-w-sm mx-auto',
                  'shadow-lg',
                  'flex items-center gap-4 -top-[6.375rem] -left-[1rem]'
                )}
              >
                <div className="w-12 h-12 flex-shrink-0 bg-primary-50 rounded-md flex items-center justify-center border border-primary-100">
                  <i className="ti ti-photo-filled text-grayscale-300 text-2xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold text-gray-900 truncate">
                    {workspaceName || 'Workspace name'}
                  </p>
                  <p className="text-sm text-gray-600">Workspace</p>
                </div>
                <div className="flex flex-col">
                  <i className="ti ti-selector text-3xl text-gray-200 font-normal" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-auto p-4 border-t border-gray-200">
          <Button
            intent="secondary"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="px-4"
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleCreateWorkspace}
            disabled={!workspaceName.trim().length || isLoading}
            className="bg-primary hover:bg-primary-600 text-white px-4"
          >
            {isLoading
              ? t('creating', 'Creating...')
              : t('add-workspace', 'Add Workspace')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
